// The builder's right-hand panel: settings for the selected block (content, style, who sees it), or
// the email's overall styles (colours, fonts, header / logo) when nothing is selected.
import { useEffect, useState } from 'react';
import RichText from './RichText.jsx';
import ImagePicker from './ImagePicker.jsx';
import { BLOCKS } from './blocks.jsx';
import { FONTS, SOCIAL_NETWORKS, BRAND_LOGO, blockPadding, resolveTheme } from '../../lib/emailRender.js';
import { fetchLookbookCollections, fetchCollectionProducts, fetchLookbookProducts } from '../../api/lookbooks.js';

export const field = 'w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30';
const CURRENCIES = ['AUD', 'NZD', 'USD', 'GBP', 'EUR', 'CAD'];

export function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-line px-4 py-3 last:border-0">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-text-muted">
        {title}
        <span className={`transition-transform ${open ? '' : '-rotate-90'}`}>▾</span>
      </button>
      {open && <div className="mt-2.5 space-y-3">{children}</div>}
    </div>
  );
}

export function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-text-secondary">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-text-muted">{hint}</span>}
    </label>
  );
}

export function Segmented({ value, options, onChange }) {
  return (
    <div className="inline-flex rounded-md border border-line bg-surface-raised p-0.5">
      {options.map(([v, l]) => (
        <button key={String(v)} type="button" onClick={() => onChange(v)} className={`rounded px-2.5 py-1 text-xs font-medium ${value === v ? 'bg-surface text-text-primary shadow-sm' : 'text-text-muted hover:text-text-primary'}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

export function Color({ value, onChange, allowClear = false, placeholder = '' }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(value || '') ? value : '#ffffff'} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-8 w-9 cursor-pointer rounded border border-line bg-surface p-0.5" />
      <input value={value || ''} placeholder={placeholder || 'none'} onChange={(e) => onChange(e.target.value.trim())} className={`${field} font-mono text-xs`} />
      {allowClear && value && (
        <button type="button" onClick={() => onChange('')} className="shrink-0 text-xs text-text-muted hover:text-text-primary">
          Clear
        </button>
      )}
    </div>
  );
}

export function Range({ value, min, max, step = 1, onChange, unit = 'px' }) {
  return (
    <div className="flex items-center gap-2">
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="flex-1 accent-accent" />
      <span className="w-12 text-right text-xs tabular-nums text-text-secondary">
        {value}
        {unit}
      </span>
    </div>
  );
}

/** Button-style image chooser: thumbnail + Change / Remove. */
function ImageField({ value, onChange, store, label = 'Image' }) {
  const [open, setOpen] = useState(false);
  return (
    <Field label={label}>
      {value ? (
        <div className="flex items-center gap-2">
          <img src={value} alt="" className="h-14 w-14 rounded border border-line object-cover" />
          <div className="flex flex-col items-start gap-1 text-xs">
            <button type="button" onClick={() => setOpen(true)} className="font-medium text-accent hover:underline">Change</button>
            <button type="button" onClick={() => onChange('', {})} className="text-text-muted hover:text-error">Remove</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen(true)} className="w-full rounded-md border border-dashed border-line-strong px-3 py-3 text-sm text-text-secondary hover:bg-surface-raised">
          Choose an image…
        </button>
      )}
      {open && (
        <ImagePicker
          store={store}
          onClose={() => setOpen(false)}
          onPick={(u, meta) => {
            onChange(u, meta || {});
            setOpen(false);
          }}
        />
      )}
    </Field>
  );
}

function CollectionPicker({ store, value, onPick }) {
  const [list, setList] = useState(null);
  useEffect(() => {
    fetchLookbookCollections(store).then(setList).catch(() => setList([]));
  }, [store]);
  return (
    <select value={value || ''} onChange={(e) => onPick((list || []).find((c) => c.id === e.target.value) || null)} className={field} disabled={!list}>
      <option value="">{list ? 'Fill from a collection…' : 'Loading collections…'}</option>
      {(list || []).map((c) => (
        <option key={c.id} value={c.id}>
          {c.title}
        </option>
      ))}
    </select>
  );
}

function ProductSearch({ store, onAdd, exclude }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (!q.trim()) return setRes([]);
    const t = setTimeout(() => fetchLookbookProducts({ store, q }).then((r) => setRes((r.products || []).filter((p) => p.image))).catch(() => setRes([])), 300);
    return () => clearTimeout(t);
  }, [q, store]);
  return (
    <div className="relative">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Add a product… (search)" className={field} />
      {res.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-line bg-surface shadow-lg">
          {res
            .filter((p) => !exclude.has(p.id))
            .map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onAdd({ id: p.id, title: p.title, image: p.image });
                  setQ('');
                }}
                className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-surface-raised"
              >
                <img src={p.image} alt="" className="h-8 w-8 rounded object-cover" />
                <span className="truncate">{p.title}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

const Align = ({ b, setS, d = 'left' }) => (
  <Field label="Alignment">
    <Segmented value={b.s?.align || d} options={[['left', 'Left'], ['center', 'Centre'], ['right', 'Right']]} onChange={(v) => setS({ align: v })} />
  </Field>
);

/** Content settings that differ per block type. */
function ContentFields({ b, set, setS, store, discountName }) {
  const [loading, setLoading] = useState(false);
  switch (b.type) {
    case 'heading':
      return (
        <>
          <p className="text-xs text-text-muted">Click the heading in the email to edit its words.</p>
          <Field label="Size">
            <Segmented value={Number(b.level) || 1} options={[[1, 'Large'], [2, 'Medium'], [3, 'Small']]} onChange={(v) => set({ level: v, size: undefined })} />
          </Field>
          <Align b={b} setS={setS} />
          <Field label="Colour">
            <Color value={b.color} onChange={(v) => set({ color: v })} allowClear placeholder="theme" />
          </Field>
        </>
      );
    case 'text':
    case 'quote':
      return (
        <>
          <p className="text-xs text-text-muted">Click the text in the email to edit it — select words to format them.</p>
          {b.type === 'quote' && (
            <Field label="Attribution">
              <input value={b.author || ''} onChange={(e) => set({ author: e.target.value })} placeholder="e.g. Sam, Bang & Strike" className={field} />
            </Field>
          )}
          {b.type === 'text' && (
            <>
              <Align b={b} setS={setS} />
              <Field label="Text size">
                <Range value={b.size || 15} min={12} max={22} onChange={(v) => set({ size: v })} />
              </Field>
            </>
          )}
          <Field label={b.type === 'quote' ? 'Accent colour' : 'Text colour'}>
            <Color value={b.color} onChange={(v) => set({ color: v })} allowClear placeholder="theme" />
          </Field>
        </>
      );
    case 'button':
      return (
        <>
          <Field label="Button text">
            <input value={b.label} onChange={(e) => set({ label: e.target.value })} className={field} />
          </Field>
          <Field label="Link" hint="Leave blank to send them to their ordering catalogue.">
            <input value={b.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://…" className={field} />
          </Field>
          <Align b={b} setS={setS} />
          <Field label="Size">
            <Segmented value={b.size || 'md'} options={[['sm', 'Small'], ['md', 'Medium'], ['lg', 'Large']]} onChange={(v) => set({ size: v })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input type="checkbox" checked={Boolean(b.full)} onChange={(e) => set({ full: e.target.checked })} className="accent-accent" /> Full width
          </label>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Button colour">
              <Color value={b.bg} onChange={(v) => set({ bg: v })} allowClear placeholder="theme" />
            </Field>
            <Field label="Text colour">
              <Color value={b.color} onChange={(v) => set({ color: v })} allowClear placeholder="theme" />
            </Field>
          </div>
          <Field label="Corner rounding">
            <Range value={b.radius ?? 8} min={0} max={30} onChange={(v) => set({ radius: v })} />
          </Field>
        </>
      );
    case 'image':
      return (
        <>
          <ImageField value={b.url} store={store} onChange={(url, meta) => set({ url, alt: b.alt || meta.alt || '' })} />
          <Field label="Description" hint="Read aloud by screen readers and shown when images are blocked.">
            <input value={b.alt} onChange={(e) => set({ alt: e.target.value })} placeholder="e.g. Model wearing the Swim Trunk in navy" className={field} />
          </Field>
          <Field label="Link when clicked (optional)">
            <input value={b.link} onChange={(e) => set({ link: e.target.value })} placeholder="https://…" className={field} />
          </Field>
          <Field label="Width">
            <Range value={b.width || 100} min={20} max={100} step={5} unit="%" onChange={(v) => set({ width: v })} />
          </Field>
          {(b.width || 100) < 100 && <Align b={b} setS={setS} />}
          <Field label="Corner rounding">
            <Range value={b.radius ?? 8} min={0} max={30} onChange={(v) => set({ radius: v })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input type="checkbox" checked={b.s?.px === 0} onChange={(e) => setS({ px: e.target.checked ? 0 : undefined })} className="accent-accent" /> Edge to edge (no side margins)
          </label>
        </>
      );
    case 'split':
      return (
        <>
          <ImageField value={b.image?.url} store={store} onChange={(url, meta) => set({ image: { ...(b.image || {}), url, alt: b.image?.alt || meta.alt || '' } })} />
          <Field label="Image description">
            <input value={b.image?.alt || ''} onChange={(e) => set({ image: { ...(b.image || {}), alt: e.target.value } })} className={field} />
          </Field>
          <Field label="Image side">
            <Segmented value={b.side || 'left'} options={[['left', 'Left'], ['right', 'Right']]} onChange={(v) => set({ side: v })} />
          </Field>
          <Field label="Text">
            <RichText html={b.html || ''} onChange={(html) => set({ html })} />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Button text">
              <input value={b.button?.label || ''} onChange={(e) => set({ button: { ...(b.button || {}), label: e.target.value } })} placeholder="none" className={field} />
            </Field>
            <Field label="Button link">
              <input value={b.button?.url || ''} onChange={(e) => set({ button: { ...(b.button || {}), url: e.target.value } })} placeholder="catalogue" className={field} />
            </Field>
          </div>
          <Field label="Vertical alignment">
            <Segmented value={b.valign || 'middle'} options={[['top', 'Top'], ['middle', 'Middle']]} onChange={(v) => set({ valign: v })} />
          </Field>
        </>
      );
    case 'columns': {
      const cols = b.cols || [];
      const setCol = (i, patch) => set({ cols: cols.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
      return (
        <>
          <Field label="Columns">
            <Segmented
              value={cols.length}
              options={[[2, 'Two'], [3, 'Three']]}
              onChange={(n) => set({ cols: n > cols.length ? [...cols, ...Array.from({ length: n - cols.length }, () => ({ image: '', title: 'Title', html: '<p>A line about it.</p>', label: '', url: '' }))] : cols.slice(0, n) })}
            />
          </Field>
          <Align b={b} setS={setS} d="left" />
          {cols.map((c, i) => (
            <div key={i} className="space-y-2 rounded-md border border-line p-2.5">
              <div className="text-xs font-semibold text-text-secondary">Column {i + 1}</div>
              <ImageField value={c.image} store={store} onChange={(url, meta) => setCol(i, { image: url, alt: c.alt || meta.alt || '' })} />
              <input value={c.title || ''} onChange={(e) => setCol(i, { title: e.target.value })} placeholder="Title" className={field} />
              <RichText html={c.html || ''} onChange={(html) => setCol(i, { html })} minHeight={50} />
              <div className="grid grid-cols-2 gap-2">
                <input value={c.label || ''} onChange={(e) => setCol(i, { label: e.target.value })} placeholder="Button text" className={field} />
                <input value={c.url || ''} onChange={(e) => setCol(i, { url: e.target.value })} placeholder="Link" className={field} />
              </div>
            </div>
          ))}
        </>
      );
    }
    case 'products': {
      const items = b.items || [];
      const count = Math.min(12, Number(b.count) || items.length || 4);
      const move = (i, d) => {
        const next = [...items];
        const [x] = next.splice(i, 1);
        next.splice(i + d, 0, x);
        set({ items: next });
      };
      // A collection loads extra products as backups: a client who doesn't stock one of the first
      // picks (with their prices on) gets the next product instead, so every email shows `count`.
      const fill = async (collectionId, n = count) => {
        setLoading(true);
        try {
          const r = await fetchCollectionProducts(store, collectionId);
          set({ collectionId, count: n, items: r.products.filter((p) => p.image).slice(0, Math.min(24, n * 2)).map((p) => ({ id: p.id, title: p.title, image: p.image })) });
        } finally {
          setLoading(false);
        }
      };
      return (
        <>
          <Field label="Section title (optional)">
            <input value={b.title || ''} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. New this season" className={field} />
          </Field>
          <Field label="How many products to show">
            <Range
              value={count}
              min={1}
              max={12}
              unit=""
              onChange={(n) => {
                // More than we have from the collection? Load more of it.
                if (b.collectionId && n * 2 > items.length && items.length < 24) fill(b.collectionId, n);
                else set({ count: n });
              }}
            />
          </Field>
          <CollectionPicker store={store} value={b.collectionId} onPick={(col) => col && fill(col.id)} />
          <ProductSearch store={store} exclude={new Set(items.map((i) => i.id))} onAdd={(p) => set({ items: [...items, p].slice(0, 24), count: Math.max(count, Math.min(12, items.length + 1)) })} />
          {loading && <p className="text-xs text-text-muted">Loading products…</p>}
          {items.length > 0 && (
            <div className="space-y-1">
              {items.map((it, i) => (
                <div key={it.id || i}>
                  {i === count && (
                    <div className="px-1 pb-1 pt-2 text-[11px] text-text-muted">
                      Backups — used {b.showPrice ? 'when a client doesn’t stock one above' : 'only if you raise the number'}
                    </div>
                  )}
                  <div className={`flex items-center gap-2 rounded border border-line px-1.5 py-1 ${i >= count ? 'opacity-50' : ''}`}>
                    <img src={it.image} alt="" className="h-8 w-8 rounded object-cover" />
                    <span className="min-w-0 flex-1 truncate text-xs">{it.title}</span>
                    <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="text-xs text-text-muted disabled:opacity-30" aria-label="Move up">↑</button>
                    <button type="button" disabled={i === items.length - 1} onClick={() => move(i, 1)} className="text-xs text-text-muted disabled:opacity-30" aria-label="Move down">↓</button>
                    <button type="button" onClick={() => set({ items: items.filter((_, j) => j !== i) })} className="text-xs text-text-muted hover:text-error" aria-label={`Remove ${it.title}`}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Field label="Per row" hint={Number(b.perRow) === 1 ? 'One per row on every screen.' : 'Phones show two per row.'}>
            <Segmented value={Number(b.perRow) || 2} options={[[1, 'One'], [2, 'Two'], [3, 'Three']]} onChange={(v) => set({ perRow: v })} />
          </Field>
          <label className="flex items-start gap-2 text-sm text-text-secondary">
            <input type="checkbox" checked={Boolean(b.showPrice)} onChange={(e) => set({ showPrice: e.target.checked })} className="mt-0.5 accent-accent" />
            <span>
              Show each client their own wholesale price
              <span className="block text-[11px] text-text-muted">From their catalogue, in their currency, with RRP. Products not in a client’s range are swapped for backups.</span>
            </span>
          </label>
          <Field label="Link text under each (optional)" hint="Every product opens its page on the wholesale platform.">
            <input value={b.buttonLabel || ''} onChange={(e) => set({ buttonLabel: e.target.value })} placeholder="e.g. Order now" className={field} />
          </Field>
          <Align b={b} setS={setS} />
        </>
      );
    }
    case 'bestsellers':
      return (
        <>
          <p className="rounded-md bg-info-light px-2.5 py-2 text-xs text-info">Personal to every client: the products <strong>they</strong> order most that are still available to them, at their own wholesale price. The canvas shows the preview client’s.</p>
          <Field label="Heading">
            <input value={b.title || ''} onChange={(e) => set({ title: e.target.value })} className={field} />
          </Field>
          <Field label="Intro (optional)">
            <input value={b.intro || ''} onChange={(e) => set({ intro: e.target.value })} placeholder="e.g. Running low on these, {firstName}?" className={field} />
          </Field>
          <Field label="How many">
            <Segmented value={Number(b.count) || 4} options={[[2, '2'], [3, '3'], [4, '4'], [6, '6']]} onChange={(v) => set({ count: v, perRow: v === 3 || v === 6 ? 3 : 2 })} />
          </Field>
          <Field label="Per row" hint={Number(b.perRow) === 1 ? 'One per row on every screen.' : 'Phones show two per row.'}>
            <Segmented value={Number(b.perRow) || 2} options={[[1, 'One'], [2, 'Two'], [3, 'Three']]} onChange={(v) => set({ perRow: v })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input type="checkbox" checked={b.showPrice !== false} onChange={(e) => set({ showPrice: e.target.checked })} className="accent-accent" /> Show their wholesale price
          </label>
          <Field label="Link text under each">
            <input value={b.buttonLabel || ''} onChange={(e) => set({ buttonLabel: e.target.value })} placeholder="e.g. Reorder" className={field} />
          </Field>
          <Field label="Clients with no order history yet">
            <Segmented value={b.fallback || 'popular'} options={[['popular', 'Show popular products'], ['hide', 'Hide the block']]} onChange={(v) => set({ fallback: v })} />
          </Field>
          {(b.fallback || 'popular') === 'popular' && (
            <Field label="Heading for them">
              <input value={b.altTitle || ''} onChange={(e) => set({ altTitle: e.target.value })} className={field} />
            </Field>
          )}
          <Align b={b} setS={setS} />
        </>
      );
    case 'countdown': {
      const local = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return new Date(d.getTime() - d.getTimezoneOffset() * 60e3).toISOString().slice(0, 16);
      };
      return (
        <>
          <p className="text-xs text-text-muted">A live timer, worked out when each client opens the email. Once the time’s up it shows “Offer ended”.</p>
          {discountName && (
            <Field label="Counts down to">
              <Segmented value={b.source === 'promotion' ? 'promotion' : 'date'} options={[['promotion', 'Promotion end'], ['date', 'A date']]} onChange={(v) => set({ source: v })} />
            </Field>
          )}
          {b.source !== 'promotion' || !discountName ? (
            <Field label="Ends at (your time)">
              <input type="datetime-local" value={local(b.endsAt)} onChange={(e) => set({ endsAt: e.target.value ? new Date(e.target.value).toISOString() : '' })} className={field} />
            </Field>
          ) : (
            <p className="text-xs text-text-secondary">Ends when “{discountName}” ends.</p>
          )}
          <Field label="Heading above">
            <input value={b.heading || ''} onChange={(e) => set({ heading: e.target.value })} placeholder="e.g. Offer ends in" className={field} />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Box colour">
              <Color value={b.box} onChange={(v) => set({ box: v })} allowClear placeholder="theme" />
            </Field>
            <Field label="Number colour">
              <Color value={b.color} onChange={(v) => set({ color: v })} allowClear placeholder="theme" />
            </Field>
          </div>
          <Field label="Corner rounding">
            <Range value={b.radius ?? 10} min={0} max={30} onChange={(v) => set({ radius: v })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input type="checkbox" checked={b.caption !== false} onChange={(e) => set({ caption: e.target.checked })} className="accent-accent" /> Show the end date underneath
          </label>
        </>
      );
    }
    case 'promotion':
      return <p className="text-xs text-text-muted">{discountName ? `Shows “${discountName}” — offer, tiers and dates — in each client’s own currency.` : 'This email isn’t linked to a promotion, so this block won’t show. Start the email from Promotions → Announce to use it.'}</p>;
    case 'lookbook':
      return (
        <>
          <CollectionPicker store={store} value={b.selection?.collectionId} onPick={(col) => set({ selection: col ? { collectionId: col.id, title: col.title } : null })} />
          {b.selection?.title && <p className="text-xs text-text-secondary">Collection: {b.selection.title}</p>}
          <Field label="Cover headline">
            <input value={b.headline || ''} onChange={(e) => set({ headline: e.target.value })} placeholder="e.g. Summer 2027" className={field} />
          </Field>
          <Field label="Intro page copy (optional)">
            <textarea value={b.intro || ''} onChange={(e) => set({ intro: e.target.value })} rows={3} className={field} />
          </Field>
          <Field label="Button text">
            <input value={b.label || ''} onChange={(e) => set({ label: e.target.value })} className={field} />
          </Field>
          <Align b={b} setS={setS} />
          <p className="text-[11px] text-text-muted">The PDF is generated when you send (or send a test); clients get a button that opens it.</p>
        </>
      );
    case 'video': {
      const yt = String(b.url || '').match(/(?:youtu\.be\/|v=|shorts\/)([\w-]{11})/);
      return (
        <>
          <Field label="Video link" hint="YouTube, Vimeo, Instagram… Email can’t play video, so it shows a thumbnail that opens it.">
            <input
              value={b.url || ''}
              onChange={(e) => {
                const u = e.target.value;
                const m = u.match(/(?:youtu\.be\/|v=|shorts\/)([\w-]{11})/);
                set({ url: u, ...(m && !b.thumbnail ? { thumbnail: `https://img.youtube.com/vi/${m[1]}/maxresdefault.jpg` } : {}) });
              }}
              placeholder="https://www.youtube.com/watch?v=…"
              className={field}
            />
          </Field>
          <ImageField label="Thumbnail" value={b.thumbnail} store={store} onChange={(u) => set({ thumbnail: u })} />
          {yt && !b.thumbnail && (
            <button type="button" onClick={() => set({ thumbnail: `https://img.youtube.com/vi/${yt[1]}/maxresdefault.jpg` })} className="text-xs font-medium text-accent hover:underline">
              Use the YouTube thumbnail
            </button>
          )}
          <Field label="Caption">
            <input value={b.label || ''} onChange={(e) => set({ label: e.target.value })} className={field} />
          </Field>
        </>
      );
    }
    case 'social': {
      const links = b.links || [];
      const used = new Set(links.map((l) => l.network));
      return (
        <>
          {links.map((l, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <span className="w-20 shrink-0 text-xs font-medium text-text-secondary">{SOCIAL_NETWORKS[l.network]}</span>
              <input value={l.url} onChange={(e) => set({ links: links.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) })} placeholder="https://…" className={field} />
              <button type="button" onClick={() => set({ links: links.filter((_, j) => j !== i) })} className="text-xs text-text-muted hover:text-error" aria-label="Remove">✕</button>
            </div>
          ))}
          <select value="" onChange={(e) => e.target.value && set({ links: [...links, { network: e.target.value, url: '' }] })} className={field}>
            <option value="">+ Add a link…</option>
            {Object.entries(SOCIAL_NETWORKS)
              .filter(([k]) => !used.has(k))
              .map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
          </select>
          <Align b={b} setS={setS} d="center" />
        </>
      );
    }
    case 'spacer':
      return (
        <Field label="Height">
          <Range value={b.height ?? 24} min={4} max={120} onChange={(v) => set({ height: v })} />
        </Field>
      );
    case 'divider':
      return (
        <>
          <Field label="Colour">
            <Color value={b.color} onChange={(v) => set({ color: v })} allowClear placeholder="theme" />
          </Field>
          <Field label="Thickness">
            <Range value={b.thickness ?? 1} min={1} max={6} onChange={(v) => set({ thickness: v })} />
          </Field>
          <Field label="Width">
            <Range value={b.width ?? 100} min={10} max={100} step={5} unit="%" onChange={(v) => set({ width: v })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input type="checkbox" checked={Boolean(b.dashed)} onChange={(e) => set({ dashed: e.target.checked })} className="accent-accent" /> Dashed
          </label>
        </>
      );
    default:
      return null;
  }
}

export function BlockInspector({ b, onChange, onDuplicate, onDelete, onSaveSection, store, discountName }) {
  const [secName, setSecName] = useState(null);
  const set = (patch) => onChange({ ...b, ...patch });
  const setS = (patch) => {
    const s = { ...(b.s || {}), ...patch };
    for (const k of Object.keys(s)) if (s[k] === undefined || s[k] === '') delete s[k];
    onChange({ ...b, s });
  };
  const pad = blockPadding(b);
  const only = b.only?.currencies || [];
  return (
    <div>
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
          <span className="text-text-muted">{BLOCKS[b.type]?.icon}</span>
          {BLOCKS[b.type]?.label || b.type}
        </div>
        <div className="flex gap-1 text-xs">
          <button type="button" onClick={onDuplicate} className="rounded px-2 py-1 text-text-secondary hover:bg-surface-raised" title="Duplicate (⌘D)">Duplicate</button>
          <button type="button" onClick={onDelete} className="rounded px-2 py-1 text-error hover:bg-error-light" title="Delete (⌫)">Delete</button>
        </div>
      </div>
      {onSaveSection && (
        <div className="border-b border-line px-4 py-2">
          {secName === null ? (
            <button type="button" onClick={() => setSecName(BLOCKS[b.type]?.label || '')} className="text-xs font-medium text-accent hover:underline">
              ☆ Save as a reusable section
            </button>
          ) : (
            <form
              className="flex gap-1"
              onSubmit={async (e) => {
                e.preventDefault();
                if (secName.trim() && (await onSaveSection(secName.trim()))) setSecName(null);
              }}
            >
              <input autoFocus value={secName} onChange={(e) => setSecName(e.target.value)} placeholder="Section name, e.g. Footer sign-off" className={`${field} py-1 text-xs`} />
              <button className="rounded bg-accent px-2 text-xs font-medium text-white">Save</button>
              <button type="button" onClick={() => setSecName(null)} className="px-1 text-xs text-text-muted">✕</button>
            </form>
          )}
        </div>
      )}
      <Section title="Content">
        <ContentFields b={b} set={set} setS={setS} store={store} discountName={discountName} />
      </Section>
      <Section title="Spacing & background" defaultOpen={false}>
        <Field label="Space above">
          <Range value={pad.pt} min={0} max={80} onChange={(v) => setS({ pt: v })} />
        </Field>
        <Field label="Space below">
          <Range value={pad.pb} min={0} max={80} onChange={(v) => setS({ pb: v })} />
        </Field>
        <Field label="Side margins">
          <Range value={pad.px} min={0} max={64} onChange={(v) => setS({ px: v })} />
        </Field>
        <Field label="Background">
          <Color value={b.s?.bg} onChange={(v) => setS({ bg: v })} allowClear />
        </Field>
      </Section>
      <Section title="Who sees it" defaultOpen={Boolean(b.s?.hide || only.length)}>
        <Field label="Show on">
          <Segmented value={b.s?.hide || ''} options={[['', 'All devices'], ['mobile', 'Desktop only'], ['desktop', 'Phone only']]} onChange={(v) => setS({ hide: v || undefined })} />
        </Field>
        <Field label="Only clients who buy in" hint={only.length ? `Hidden from everyone else — e.g. a UK-only message.` : 'Everyone sees this block.'}>
          <div className="flex flex-wrap gap-1.5">
            {CURRENCIES.map((cur) => (
              <button
                key={cur}
                type="button"
                onClick={() => {
                  const next = only.includes(cur) ? only.filter((x) => x !== cur) : [...only, cur];
                  const nb = { ...b };
                  if (next.length) nb.only = { currencies: next };
                  else delete nb.only;
                  onChange(nb);
                }}
                className={`rounded-full px-2.5 py-1 text-xs ${only.includes(cur) ? 'bg-accent text-white' : 'bg-surface-raised text-text-secondary hover:text-text-primary'}`}
              >
                {cur}
              </button>
            ))}
          </div>
        </Field>
      </Section>
    </div>
  );
}

// --- theme ------------------------------------------------------------------------------------------

export function ThemePanel({ style, onChange, store, onSaveBrand, brandMsg }) {
  const T = resolveTheme(style);
  const raw = style || {};
  const set = (patch) => onChange({ ...raw, ...patch });
  const setH = (patch) => onChange({ ...raw, header: { ...(raw.header || {}), ...patch } });
  const [logoOpen, setLogoOpen] = useState(false);
  const fontOpts = Object.entries(FONTS).map(([k, f]) => (
    <option key={k} value={k}>
      {f.label}
    </option>
  ));
  return (
    <div>
      <div className="border-b border-line px-4 py-3">
        <div className="text-sm font-semibold text-text-primary">Email styles</div>
        <p className="mt-0.5 text-xs text-text-muted">Applies to the whole email. Select a block to edit it.</p>
      </div>
      <Section title="Header">
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input type="checkbox" checked={T.header.show} onChange={(e) => setH({ show: e.target.checked })} className="accent-accent" /> Show a header above the email
        </label>
        {T.header.show && (
          <>
            <Field label="Logo">
              {T.header.logoUrl ? (
                <div className="flex flex-wrap items-center gap-2">
                  <img src={T.header.logoUrl} alt="" className="h-8 max-w-[120px] rounded border border-line bg-surface-raised object-contain p-1" />
                  <button type="button" onClick={() => setLogoOpen(true)} className="text-xs font-medium text-accent hover:underline">Change</button>
                  {T.header.logoUrl !== BRAND_LOGO && (
                    <button type="button" onClick={() => setH({ logoUrl: BRAND_LOGO })} className="text-xs text-text-muted hover:text-text-primary">TEAMM8 logo</button>
                  )}
                  <button type="button" onClick={() => setH({ logoUrl: '' })} className="text-xs text-text-muted hover:text-error">Use text</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button type="button" onClick={() => setH({ logoUrl: BRAND_LOGO })} className="flex-1 rounded-md border border-line px-3 py-2 text-sm text-text-secondary hover:bg-surface-raised">TEAMM8 logo</button>
                  <button type="button" onClick={() => setLogoOpen(true)} className="flex-1 rounded-md border border-dashed border-line-strong px-3 py-2 text-sm text-text-secondary hover:bg-surface-raised">Upload…</button>
                </div>
              )}
            </Field>
            {T.header.logoUrl ? (
              <>
                <Field label="Logo width">
                  <Range value={T.header.logoWidth} min={60} max={300} onChange={(v) => setH({ logoWidth: v })} />
                </Field>
                <Field label="Label next to the logo (optional)">
                  <input value={T.header.subtext} onChange={(e) => setH({ subtext: e.target.value })} placeholder="e.g. Wholesale" className={field} />
                </Field>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Field label="Wordmark">
                  <input value={T.header.text} onChange={(e) => setH({ text: e.target.value })} className={field} />
                </Field>
                <Field label="Tagline">
                  <input value={T.header.subtext} onChange={(e) => setH({ subtext: e.target.value })} className={field} />
                </Field>
              </div>
            )}
            <Field label="Position">
              <Segmented value={T.header.align} options={[['left', 'Left'], ['center', 'Centre'], ['right', 'Right']]} onChange={(v) => setH({ align: v })} />
            </Field>
            <Field label="Logo links to (optional)">
              <input value={T.header.link} onChange={(e) => setH({ link: e.target.value })} placeholder="https://teamm8.com" className={field} />
            </Field>
          </>
        )}
        {logoOpen && (
          <ImagePicker
            store={store}
            onClose={() => setLogoOpen(false)}
            onPick={(u) => {
              setH({ logoUrl: u });
              setLogoOpen(false);
            }}
          />
        )}
      </Section>
      <Section title="Colours">
        <div className="grid grid-cols-2 gap-x-2 gap-y-3">
          <Field label="Background">
            <Color value={T.bg} onChange={(v) => set({ bg: v })} />
          </Field>
          <Field label="Email body">
            <Color value={T.contentBg} onChange={(v) => set({ contentBg: v })} />
          </Field>
          <Field label="Headings">
            <Color value={T.heading} onChange={(v) => set({ heading: v })} />
          </Field>
          <Field label="Text">
            <Color value={T.text} onChange={(v) => set({ text: v })} />
          </Field>
          <Field label="Buttons">
            <Color value={T.accent} onChange={(v) => set({ accent: v })} />
          </Field>
          <Field label="Button text">
            <Color value={T.buttonText} onChange={(v) => set({ buttonText: v })} />
          </Field>
          <Field label="Links">
            <Color value={T.link} onChange={(v) => set({ link: v })} />
          </Field>
          <Field label="Lines & borders">
            <Color value={T.line} onChange={(v) => set({ line: v })} />
          </Field>
        </div>
      </Section>
      <Section title="Typography">
        <Field label="Heading font">
          <select value={T.headingFont} onChange={(e) => set({ headingFont: e.target.value })} className={field}>
            {fontOpts}
          </select>
        </Field>
        <Field label="Body font" hint="Web fonts show in Apple Mail and iOS; other inboxes fall back to a similar safe font.">
          <select value={T.font} onChange={(e) => set({ font: e.target.value })} className={field}>
            {fontOpts}
          </select>
        </Field>
        <Field label="Body text size">
          <Range value={T.fontSize} min={13} max={18} onChange={(v) => set({ fontSize: v })} />
        </Field>
      </Section>
      <Section title="Layout" defaultOpen={false}>
        <Field label="Email width">
          <Range value={T.width} min={500} max={680} step={10} onChange={(v) => set({ width: v })} />
        </Field>
        <Field label="Body corner rounding">
          <Range value={T.radius} min={0} max={24} onChange={(v) => set({ radius: v })} />
        </Field>
        <Field label="Button rounding">
          <Range value={T.buttonRadius} min={0} max={30} onChange={(v) => set({ buttonRadius: v })} />
        </Field>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input type="checkbox" checked={T.border} onChange={(e) => set({ border: e.target.checked })} className="accent-accent" /> Border around the email body
        </label>
        <Field label="Footer note (optional)" hint="Shown above the standard footer and unsubscribe link.">
          <textarea value={raw.footerNote || ''} onChange={(e) => set({ footerNote: e.target.value })} rows={2} className={field} />
        </Field>
      </Section>
      <div className="space-y-2 px-4 py-3">
        <button type="button" onClick={onSaveBrand} className="w-full rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised">
          Save as our brand style
        </button>
        <button type="button" onClick={() => onChange({})} className="w-full rounded-md px-3 py-1.5 text-xs text-text-muted hover:text-text-primary">
          Reset to TEAMM8 default
        </button>
        {brandMsg && <p className="text-center text-xs text-success">{brandMsg}</p>}
        <p className="text-[11px] text-text-muted">New emails start with your brand style.</p>
      </div>
    </div>
  );
}
